import {
  HiOutlineExclamationTriangle,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
  HiOutlineTicket,
} from "react-icons/hi2";
import { IHomeTileConfig } from "@/api_framework/api_modals/HomeTileConfig";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import { SearchInput } from "../controls";
import { primaryButtonClassName, secondaryButtonClassName } from "../interface";
import { ConfirmModal, PersonaExpressionText } from "../components";
import { fromPersonaRule } from "../personaExpression";

export const ConfigFilters = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm: string;
  onSearchChange: (next: string) => void;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-slate-100 bg-slate-50/60 px-5 py-3">
    <SearchInput
      value={searchTerm}
      onChange={onSearchChange}
      placeholder="Search persona, ribbon or user"
      ariaLabel="Search tile configurations"
      clearLabel="Clear configuration search"
    />
  </div>
);

const EmptyConfigState = ({
  hasFilters,
  tileName,
  onClearFilters,
  onAddRule,
}: {
  hasFilters: boolean;
  tileName?: string;
  onClearFilters: () => void;
  onAddRule: () => void;
}) => (
  <div className="flex flex-col items-center px-5 py-14 text-center">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF5FF] text-[#193A6A]">
      {hasFilters ? <HiOutlineMagnifyingGlass className="h-6 w-6" /> : <HiOutlineTicket className="h-6 w-6" />}
    </span>
    <h3 className="mt-3 text-sm font-semibold text-gray-900">
      {hasFilters ? "No configuration found" : "No configuration yet"}
    </h3>
    <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
      {hasFilters
        ? "No configuration matches the current search or filters. Try clearing them."
        : `No ribbon has been configured for ${tileName} yet.`}
    </p>
    {hasFilters ? (
      <button type="button" onClick={onClearFilters} className={`mt-4 ${secondaryButtonClassName}`}>
        Clear filters
      </button>
    ) : (
      <button type="button" onClick={onAddRule} className={`mt-4 ${primaryButtonClassName}`}>
        <HiOutlinePlus className="h-4 w-4" />
        Add Rule
      </button>
    )}
  </div>
);

export const ConfigTable = ({
  columns,
  configs,
  isLoading,
  error,
  hasFilters,
  tileName,
  onRetry,
  onClearFilters,
  onAddRule,
}: {
  columns: TableColumn<IHomeTileConfig>[];
  configs: IHomeTileConfig[];
  isLoading: boolean;
  error: string | null;
  hasFilters: boolean;
  tileName?: string;
  onRetry: () => void;
  onClearFilters: () => void;
  onAddRule: () => void;
}) => {
  if (isLoading) {
    return (
      <div className="px-5 py-4">
        <StructuredTableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center px-5 py-14 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
          <HiOutlineExclamationTriangle className="h-6 w-6" />
        </span>
        <h3 className="mt-3 text-sm font-semibold text-gray-900">{error}</h3>
        <button type="button" onClick={onRetry} className={`mt-4 ${secondaryButtonClassName}`}>
          Retry
        </button>
      </div>
    );
  }

  if (!configs.length) {
    return (
      <EmptyConfigState
        hasFilters={hasFilters}
        tileName={tileName}
        onClearFilters={onClearFilters}
        onAddRule={onAddRule}
      />
    );
  }

  return (
    <TableComponent<IHomeTileConfig>
      columns={columns}
      data={configs}
      getRowId={(row) => row.id}
      containerClassName="!mr-0 !rounded-none !border-0 bg-white"
      scrollContainerClassName="overflow-x-auto max-h-[calc(100vh-320px)]"
      minTableWidthClassName="min-w-full"
      tableClassName="min-w-full divide-y divide-slate-100"
      stickyHeader
      wrapCells
    />
  );
};

export const DeleteRuleModal = ({
  target,
  tileName,
  isLoading,
  onConfirm,
  onClose,
}: {
  target: IHomeTileConfig | null;
  tileName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <ConfirmModal
    isOpen={Boolean(target)}
    title="Delete this rule?"
    description={
      <>
        {target?.persona_rule && (
          <span className="mb-1.5 block rounded-md bg-slate-50 px-2 py-1">
            <PersonaExpressionText value={fromPersonaRule(target.persona_rule)} />
          </span>
        )}
        The ribbon{target?.ribbon_text ? ` “${target.ribbon_text}”` : ""} will stop showing on {tileName}
        {target?.persona_rule ? " for the selected persona combination" : ""}. This cannot be undone.
      </>
    }
    isLoading={isLoading}
    onConfirm={onConfirm}
    onClose={onClose}
  />
);
